import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import InstallAppButton from '../components/shared/InstallAppButton';

// Split-screen layout: left = rolling-hills nature splash, right = clean white form card.
// Palette: sky blue → light-yellow meadow → medium/light green grass.
const HILLS_BG_IMAGE =
  'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxyb2xsaW5nJTIwZ3JlZW4lMjBoaWxscyUyMGJsdWUlMjBza3l8ZW58MHx8fHwxNzc2ODMxNDgwfDA&ixlib=rb-4.1.0&q=85';

const LoginPage = ({ onSwitch }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password, rememberMe);
    } catch (err) {
      if (err.message === 'Network error') {
        setError('Unable to reach the server. Please check your internet connection and try again. If this persists, wait 1-2 minutes and refresh.');
      } else {
        setError(err.message);
      }
    }
    setLoading(false);
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin;
    try {
      localStorage.setItem('oauth_pending', Date.now().toString());
      localStorage.setItem('remember_me', rememberMe ? 'true' : 'false');
    } catch (e) { /* ignore */ }
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      data-testid="login-page"
      style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: '1fr',
        background: 'linear-gradient(180deg, #C4B5FD 0%, #E0F2FE 20%, #FEF9C3 50%, #BBF7D0 100%)',
      }}
      className="login-split"
    >
      {/* LEFT: Rolling Hills splash (hidden on mobile) */}
      <aside
        className="hidden md:flex"
        style={{
          position: 'relative',
          minHeight: '100vh',
          backgroundImage: `linear-gradient(180deg, rgba(186, 230, 253, 0.72) 0%, rgba(254, 249, 195, 0.45) 45%, rgba(134, 239, 172, 0.55) 100%), url("${HILLS_BG_IMAGE}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '48px',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h1 style={{
            fontFamily: 'Outfit, "IBM Plex Sans", sans-serif',
            fontSize: '3rem', fontWeight: 900, letterSpacing: '-0.02em',
            color: '#0F172A',
            margin: 0,
            textShadow: '0 2px 12px rgba(255,255,255,0.7)',
          }}>
            InfoPilot
          </h1>
          <p style={{
            color: '#0F172A',
            fontSize: '1.05rem',
            marginTop: 8,
            maxWidth: 380,
            fontWeight: 500,
            textShadow: '0 1px 6px rgba(255,255,255,0.6)',
          }}>
            A calm, natural search workspace. Protocols, maps, and social — under open skies and over rolling hills.
          </p>
        </div>
        <div style={{
          background: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(14px)',
          borderRadius: 16,
          padding: '18px 22px',
          border: '1px solid rgba(16, 185, 129, 0.32)',
          boxShadow: '0 8px 24px rgba(14, 165, 233, 0.15)',
          maxWidth: 440,
        }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#047857', letterSpacing: '0.15em' }}>
            WELCOME BACK
          </div>
          <div style={{ fontFamily: 'Outfit', fontSize: '1.4rem', fontWeight: 700, color: '#0F172A', marginTop: 4 }}>
            Sign in and keep exploring.
          </div>
        </div>
      </aside>

      {/* RIGHT: Clean white form card */}
      <main style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '32px 20px',
      }}>
        <div style={{
          width: '100%', maxWidth: 420,
          background: '#ffffff',
          borderRadius: 20,
          padding: '32px 28px',
          border: '1px solid rgba(14, 165, 233, 0.25)',
          boxShadow: '0 20px 60px rgba(14, 165, 233, 0.12)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <h2 style={{
              fontFamily: 'Outfit, "IBM Plex Sans", sans-serif',
              fontSize: '1.8rem', fontWeight: 800, margin: 0, color: '#0F172A',
            }}>Welcome back 🌿</h2>
            <p style={{ color: '#475569', fontSize: '0.9rem', margin: '6px 0 0' }}>
              Sign in to your InfoPilot account
            </p>
          </div>

          {error && (
            <div data-testid="login-error" style={{
              color: '#B91C1C', textAlign: 'center', fontSize: '0.85rem',
              padding: '10px 12px', background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 10, marginBottom: 12,
            }}>{error}</div>
          )}

          <InstallAppButton variant="banner" />

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              type="email" placeholder="Email" value={email}
              onChange={(e) => setEmail(e.target.value)} required
              data-testid="login-email-input"
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 12,
                border: '1px solid rgba(14, 165, 233, 0.25)',
                background: '#F0FDF9', color: '#0F172A',
                fontSize: '1rem', outline: 'none', boxSizing: 'border-box',
              }}
            />
            <input
              type="password" placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)} required
              data-testid="login-password-input"
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 12,
                border: '1px solid rgba(14, 165, 233, 0.25)',
                background: '#F0FDF9', color: '#0F172A',
                fontSize: '1rem', outline: 'none', boxSizing: 'border-box',
              }}
            />

            <button
              type="button"
              data-testid="remember-me-toggle"
              onClick={() => setRememberMe(!rememberMe)}
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 12,
                display: 'flex', alignItems: 'center', gap: 12,
                cursor: 'pointer', fontSize: '0.95rem', fontWeight: 600,
                color: rememberMe ? '#047857' : '#64748B',
                background: rememberMe ? 'rgba(16, 185, 129, 0.12)' : '#F8FAFC',
                border: rememberMe ? '2px solid #10B981' : '2px solid #E2E8F0',
                transition: 'all 0.2s',
              }}
            >
              <div style={{
                width: 44, height: 24, minWidth: 44, borderRadius: 12,
                background: rememberMe ? '#10B981' : '#CBD5E1',
                position: 'relative', transition: 'background 0.2s',
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: '#fff', position: 'absolute', top: 3,
                  left: rememberMe ? 23 : 3,
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                }} />
              </div>
              <span>Keep me logged in for 1 month!</span>
            </button>

            <button
              type="submit" disabled={loading}
              data-testid="login-submit-btn"
              style={{
                width: '100%', padding: '14px', borderRadius: 999, border: 'none',
                background: 'linear-gradient(135deg, #10B981, #059669)',
                color: '#ffffff', fontSize: '1rem', fontWeight: 800,
                cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
                boxShadow: '0 8px 20px rgba(16, 185, 129, 0.35)',
                transition: 'transform 0.1s ease, box-shadow 0.2s ease',
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0' }}>
            <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
            <span style={{ padding: '0 12px', color: '#94A3B8', fontSize: '0.82rem' }}>or</span>
            <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
          </div>

          <button
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            style={{
              width: '100%', padding: '12px', borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              background: '#ffffff', border: '1px solid #E2E8F0',
              color: '#0F172A', fontSize: '0.95rem', fontWeight: 500, cursor: 'pointer',
              transition: 'border-color 0.2s, box-shadow 0.2s',
            }}
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" style={{ width: 18 }} />
            Continue with Google
          </button>

          <div style={{ textAlign: 'center', marginTop: 16, fontSize: '0.85rem', color: '#475569' }}>
            Need an account?{' '}
            <a href="#" onClick={(e) => { e.preventDefault(); onSwitch(); }}
              data-testid="switch-to-register"
              style={{ color: '#047857', textDecoration: 'none', fontWeight: 700 }}>
              Sign Up
            </a>
          </div>
        </div>
      </main>

      <style>{`
        @media (min-width: 768px) {
          .login-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
